import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { COLORS } from '../theme';

const SUSPENSION_TYPES = [
  { value: 'three_strikes', label: 'Three strikes (contact violations)' },
  { value: 'fraud', label: 'Fraud or scam' },
  { value: 'inappropriate_behavior', label: 'Inappropriate behavior' },
  { value: 'spam', label: 'Spam or abuse' },
  { value: 'security_risk', label: 'Security risk' },
  { value: 'accidental', label: 'Accidental — undo' },
  { value: 'other', label: 'Other' },
];

const REACTIVATION_TYPES = [
  { value: 'issue_resolved', label: 'Issue resolved' },
  { value: 'suspended_in_error', label: 'Suspended in error' },
  { value: 'corrected_behavior', label: 'Worker corrected behavior' },
  { value: 'investigation_cleared', label: 'Investigation cleared' },
  { value: 'strikes_forgiven', label: 'Strikes forgiven' },
  { value: 'other', label: 'Other' },
];

const SUSPENSION_TYPE_LABELS = Object.fromEntries(SUSPENSION_TYPES.map(t => [t.value, t.label]));

const INPUT_STYLE = {
  color: '#1F2937',
  backgroundColor: 'white',
  border: '1px solid #D1D5DB',
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

function StatusBadge({ status }) {
  const map = {
    active: { bg: '#34A85326', text: COLORS.success, label: 'Active' },
    suspended: { bg: '#EA433526', text: COLORS.danger, label: 'Suspended' },
    warned: { bg: '#F5A62326', text: COLORS.warning, label: 'Warned' },
    pending_email_verification: { bg: '#F5A62326', text: COLORS.warning, label: 'Pending' },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary, label: status || '—' };
  return (
    <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
      {s.label}
    </span>
  );
}

function JobStatusBadge({ status }) {
  const map = {
    active: { bg: '#34A85326', text: COLORS.success },
    closed: { bg: '#EA433526', text: COLORS.danger },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary };
  return (
    <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
      {status || '—'}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{
      flex: 1,
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '26px', fontWeight: '700', color: COLORS.accent, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>{label}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: value ? COLORS.textPrimary : COLORS.textMuted }}>{value || '—'}</div>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatRelative(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function EmployerDetailPage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [hiresCount, setHiresCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [workerNames, setWorkerNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [modal, setModal] = useState(null); // { mode: 'suspend' | 'reactivate' | 'clear_mfa' }
  const [modalType, setModalType] = useState('');
  const [modalReason, setModalReason] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [toast, setToast] = useState('');

  // HC-SECURITY-012 (Prompt #339) — MFA status, fetched separately from the
  // main user doc since MFA enrollment lives in Auth, not Firestore, and a
  // client can never read another user's Auth record directly.
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(true);

  async function loadMfaStatus() {
    setMfaStatusLoading(true);
    try {
      const fn = httpsCallable(functions, 'getEmployerMfaStatus');
      const result = await fn({ targetUid: uid });
      setMfaStatus(result.data);
    } catch (err) {
      setMfaStatus(null);
    } finally {
      setMfaStatusLoading(false);
    }
  }

  useEffect(() => {
    loadMfaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, 'users', uid));
      if (!snap.exists()) { setNotFound(true); setLoading(false); return; }
      setUser({ id: snap.id, ...snap.data() });

      const [jobsSnap, hiresSnap, convsSnap] = await Promise.all([
        getDocs(query(collection(db, 'jobs'), where('employerId', '==', uid))),
        getDocs(query(collection(db, 'applications'), where('employerId', '==', uid), where('status', '==', 'hired'))),
        getDocs(query(collection(db, 'conversations'), where('employerId', '==', uid))),
      ]);

      setJobs(jobsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));

      setHiresCount(hiresSnap.size);

      const sortedConvs = convsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.lastMessageTime?.toMillis?.() || 0) - (a.lastMessageTime?.toMillis?.() || 0))
        .slice(0, 10);
      setConversations(sortedConvs);

      const candidateIds = [...new Set(sortedConvs.map(c => c.candidateId).filter(Boolean))];
      if (candidateIds.length > 0) {
        const userSnaps = await Promise.all(candidateIds.map(id => getDoc(doc(db, 'users', id))));
        const names = {};
        userSnaps.forEach((s, i) => { if (s.exists()) names[candidateIds[i]] = s.data().displayName || null; });
        setWorkerNames(names);
      }

      setLoading(false);
    }
    load();
  }, [uid]);

  async function reloadUser() {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) setUser({ id: snap.id, ...snap.data() });
  }

  function openModal(mode) {
    setModal({ mode });
    setModalType('');
    setModalReason('');
    setModalPassword('');
    setModalError('');
  }

  async function submitModal() {
    const requiresReason = modal.mode === 'suspend' || modal.mode === 'reactivate';
    if (requiresReason) {
      if (!modalType) { setModalError('Please select a reason.'); return; }
      if (modalReason.trim().length < 10) { setModalError('Details must be at least 10 characters.'); return; }
    }
    if (!modalPassword) { setModalError('Password is required.'); return; }
    setModalLoading(true);
    setModalError('');
    try {
      if (modal.mode === 'suspend') {
        const fn = httpsCallable(functions, 'adminSuspendAccount');
        await fn({ targetUid: uid, password: modalPassword, suspensionType: modalType, suspensionReason: modalReason.trim() });
      } else if (modal.mode === 'reactivate') {
        const fn = httpsCallable(functions, 'adminReactivateAccount');
        await fn({ targetUid: uid, password: modalPassword, reactivationType: modalType, reactivationReason: modalReason.trim() });
      } else {
        const fn = httpsCallable(functions, 'adminClearEmployerMfa');
        await fn({ targetUid: uid, password: modalPassword });
      }
      setToast(modal.mode === 'suspend' ? 'Account suspended' : modal.mode === 'reactivate' ? 'Account reactivated' : 'MFA cleared');
      setTimeout(() => setToast(''), 3000);
      setModal(null);
      await reloadUser();
      if (modal.mode === 'clear_mfa') await loadMfaStatus();
    } catch (err) {
      setModalPassword('');
      setModalError(err.message || 'Action failed. Check your password and try again.');
    } finally {
      setModalLoading(false);
    }
  }

  if (loading) return <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Loading…</div>;
  if (notFound) return <div style={{ color: COLORS.danger, fontSize: '13px' }}>Employer not found.</div>;

  const activeJobs = jobs.filter(j => j.status === 'active').length;
  const totalApplicants = jobs.reduce((sum, j) => sum + (j.applicantCount || 0), 0);
  const typeOptions = modal?.mode === 'suspend' ? SUSPENSION_TYPES : REACTIVATION_TYPES;

  const card = { backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '20px' };
  const sectionLabel = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '16px' };
  const fieldLabel = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: COLORS.success, color: 'white',
          padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Suspend / Reactivate Modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: COLORS.surface, borderRadius: '12px', padding: '28px',
            width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '6px' }}>
              {modal.mode === 'suspend' ? 'Suspend Account' : modal.mode === 'reactivate' ? 'Reactivate Account' : 'Clear MFA'}
            </div>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '20px' }}>
              {modal.mode === 'clear_mfa'
                ? 'This clears the employer’s MFA enrollment. They will need to re-enroll on next login. Re-enter your password to confirm.'
                : 'Re-enter your password and provide a reason. This action will be logged.'}
            </div>

            {modal.mode !== 'clear_mfa' && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={fieldLabel}>Reason</label>
                  <select value={modalType} onChange={e => setModalType(e.target.value)} style={INPUT_STYLE}>
                    <option value="">Select a reason…</option>
                    {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={fieldLabel}>Details</label>
                  <textarea
                    value={modalReason}
                    onChange={e => setModalReason(e.target.value)}
                    placeholder="Minimum 10 characters"
                    rows={3}
                    style={{ ...INPUT_STYLE, resize: 'vertical' }}
                  />
                </div>
              </>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={fieldLabel}>Your Password</label>
              <input
                type="password"
                autoComplete="current-password"
                value={modalPassword}
                onChange={e => setModalPassword(e.target.value)}
                style={INPUT_STYLE}
              />
            </div>

            {modalError && (
              <div style={{ fontSize: '12px', color: COLORS.danger, marginBottom: '12px' }}>
                {modalError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setModal(null)}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500',
                  border: `1px solid ${COLORS.border}`, borderRadius: '6px',
                  backgroundColor: 'transparent', color: COLORS.textSecondary, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitModal}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  backgroundColor: modal.mode === 'suspend' || modal.mode === 'clear_mfa' ? COLORS.danger : COLORS.success,
                  color: 'white',
                  opacity: modalLoading ? 0.6 : 1,
                }}
              >
                {modalLoading ? '…' : modal.mode === 'suspend' ? 'Suspend' : modal.mode === 'reactivate' ? 'Reactivate' : 'Clear MFA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back + action */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/employers')}
          style={{ background: 'none', border: 'none', color: COLORS.accent, fontSize: '13px', cursor: 'pointer', padding: 0, fontWeight: '500' }}
        >
          ← Employers
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          {user.accountStatus === 'suspended' ? (
            <button
              onClick={() => openModal('reactivate')}
              style={{
                backgroundColor: COLORS.success, color: 'white',
                border: 'none', borderRadius: '6px',
                padding: '7px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              Reactivate Account
            </button>
          ) : user.accountStatus === 'active' ? (
            <button
              onClick={() => openModal('suspend')}
              style={{
                backgroundColor: COLORS.danger, color: 'white',
                border: 'none', borderRadius: '6px',
                padding: '7px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              Suspend Account
            </button>
          ) : null}
        </div>
      </div>

      {/* Name + email */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary }}>{user.displayName || '(No name)'}</div>
        <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>{user.email || '—'}</div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        <StatCard label="Total Jobs" value={jobs.length} />
        <StatCard label="Active Jobs" value={activeJobs} />
        <StatCard label="Total Applicants" value={totalApplicants} />
        <StatCard label="Hires" value={hiresCount} />
      </div>

      {/* Two-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px', alignItems: 'start' }}>
        {/* Left: account info */}
        <div style={card}>
          <div style={sectionLabel}>Account</div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '6px' }}>Status</div>
            <StatusBadge status={user.accountStatus} />
          </div>

          {user.accountStatus === 'suspended' && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#EA433510', borderRadius: '6px', border: '1px solid #EA433330' }}>
              <div style={{ fontSize: '11px', color: COLORS.danger, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '10px' }}>
                Suspension Details
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Reason</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>{SUSPENSION_TYPE_LABELS[user.suspensionType] || user.suspensionType || '—'}</div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Details</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>{user.suspensionReason || '—'}</div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Suspended by</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>
                  {user.suspendedBy === 'system' ? 'System (auto)' : (user.suspendedByEmail || user.suspendedBy || '—')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Suspended at</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>{formatDate(user.suspendedAt)}</div>
              </div>
            </div>
          )}

          <Field label="Access Level" value={user.accessLevel} />
          <Field label="Company" value={user.companyName} />
          <Field label="Phone" value={user.phone} />
          <Field label="Member Since" value={formatDate(user.createdAt)} />

          {/* HC-SECURITY-012 (Prompt #339) — MFA status + Clear MFA action */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '6px' }}>MFA</div>
            {mfaStatusLoading ? (
              <div style={{ fontSize: '12px', color: COLORS.textMuted }}>Loading…</div>
            ) : !mfaStatus ? (
              <div style={{ fontSize: '12px', color: COLORS.textMuted }}>Unable to load MFA status.</div>
            ) : mfaStatus.enrolled ? (
              <>
                <div style={{ fontSize: '13px', color: COLORS.textPrimary, marginBottom: '6px' }}>
                  Enrolled — {mfaStatus.maskedPhone || '(non-phone factor)'}
                  {mfaStatus.enrollmentDate && ` — since ${formatDate(mfaStatus.enrollmentDate)}`}
                </div>
                <button
                  onClick={() => openModal('clear_mfa')}
                  style={{ background: 'none', border: 'none', color: COLORS.danger, fontSize: '12px', cursor: 'pointer', padding: 0, fontWeight: '500' }}
                >
                  Clear MFA
                </button>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: COLORS.textMuted }}>Not enrolled</div>
            )}
          </div>

          <div style={{ marginBottom: 0 }}>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>UID</div>
            <div style={{ fontSize: '11px', color: COLORS.textMuted, wordBreak: 'break-all', fontFamily: 'monospace' }}>{uid}</div>
          </div>
        </div>

        {/* Right: jobs + conversations stacked */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Jobs card */}
          <div style={card}>
            <div style={sectionLabel}>Jobs ({jobs.length})</div>
            {jobs.length === 0 ? (
              <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>No jobs posted.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 110px', borderBottom: `1px solid ${COLORS.border}`, paddingBottom: '8px', marginBottom: '4px' }}>
                  {['Title', 'Status', 'Posted'].map(h => (
                    <div key={h} style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>{h}</div>
                  ))}
                </div>
                {jobs.map((j, i) => (
                  <div key={j.id} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 110px', alignItems: 'center', padding: '10px 0', borderBottom: i < jobs.length - 1 ? `1px solid ${COLORS.border}30` : 'none' }}>
                    <div style={{ fontSize: '13px', color: COLORS.textPrimary, fontWeight: '500' }}>{j.title || '—'}</div>
                    <div><JobStatusBadge status={j.status} /></div>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{formatDate(j.createdAt)}</div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Recent conversations card */}
          <div style={card}>
            <div style={sectionLabel}>Recent Conversations ({conversations.length})</div>
            {conversations.length === 0 ? (
              <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>No conversations yet.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '130px 130px 1fr 90px', borderBottom: `1px solid ${COLORS.border}`, paddingBottom: '8px', marginBottom: '4px' }}>
                  {['Worker', 'Job', 'Last Message', 'Time'].map(h => (
                    <div key={h} style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>{h}</div>
                  ))}
                </div>
                {conversations.map((c, i) => {
                  const text = c.lastMessage?.text;
                  const preview = text ? text.slice(0, 60) + (text.length > 60 ? '…' : '') : '—';
                  return (
                    <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '130px 130px 1fr 90px', alignItems: 'center', padding: '10px 0', borderBottom: i < conversations.length - 1 ? `1px solid ${COLORS.border}30` : 'none' }}>
                      <div style={{ fontSize: '13px', color: COLORS.textPrimary, fontWeight: '500' }}>{workerNames[c.candidateId] || 'Unknown'}</div>
                      <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{c.jobTitle || '—'}</div>
                      <div style={{ fontSize: '12px', color: COLORS.textMuted, paddingRight: '12px' }}>{preview}</div>
                      <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{formatRelative(c.lastMessageTime)}</div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
